import type {
  BusinessProfile,
  Customer,
  Quote,
  QuotePreset,
  Service,
  ServiceCategory,
  Settings,
} from '@/types';
import { APP_CONFIG } from '@/config/app.config';
import { DEFAULT_PLAN } from '@/config/plans';
import type { StorageAdapter } from '../adapters';
import { createDocumentStore, type DocumentStore } from '../createDocumentStore';
import { createRepository, type Repository } from '../createRepository';
import { STORAGE_KEYS } from '../keys';

export const DEFAULT_SETTINGS: Settings = {
  schemaVersion: APP_CONFIG.schemaVersion,
  theme: 'system',
  plan: DEFAULT_PLAN,
};

/** Autosaved work-in-progress, one draft per business. */
export interface DraftEnvelope {
  drafts: Record<string, { savedAt: string; draft: unknown }>;
}

export const EMPTY_DRAFTS: DraftEnvelope = { drafts: {} };

export interface DataStore {
  businesses: Repository<BusinessProfile>;
  categories: Repository<ServiceCategory>;
  services: Repository<Service>;
  presets: Repository<QuotePreset>;
  customers: Repository<Customer>;
  quotes: Repository<Quote>;
  settings: DocumentStore<Settings>;
  drafts: DocumentStore<DraftEnvelope>;
}

/**
 * Builds the entire data layer from a single adapter. Swapping persistence
 * backends means passing a different adapter here — nothing else moves.
 */
export function createDataStore(adapter: StorageAdapter): DataStore {
  return {
    businesses: createRepository<BusinessProfile>(adapter, STORAGE_KEYS.businesses, 'Business', 'biz'),
    categories: createRepository<ServiceCategory>(adapter, STORAGE_KEYS.categories, 'Category', 'cat'),
    services: createRepository<Service>(adapter, STORAGE_KEYS.services, 'Service', 'svc'),
    presets: createRepository<QuotePreset>(adapter, STORAGE_KEYS.presets, 'Preset', 'pre'),
    customers: createRepository<Customer>(adapter, STORAGE_KEYS.customers, 'Customer', 'cus'),
    quotes: createRepository<Quote>(adapter, STORAGE_KEYS.quotes, 'Quote', 'qte'),
    settings: createDocumentStore<Settings>(adapter, STORAGE_KEYS.settings, DEFAULT_SETTINGS),
    drafts: createDocumentStore<DraftEnvelope>(adapter, STORAGE_KEYS.draft, EMPTY_DRAFTS),
  };
}
